import React, { useEffect, useRef, useState } from "react";
import {
  Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, IconButton, Tab, Tabs, Tooltip, Typography,
} from "@mui/material";
import {
  AttachFileOutlined, DeleteOutline, DownloadOutlined, SaveOutlined, VisibilityOutlined,
} from "@mui/icons-material";
import DataTable from "../../../components/common/tables/DataTable";
import { subcontractApi } from "../services/subcontractApi";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppSelect, AppTextField } from "components/common/inputs";
import SubcontractAssigneeAutocomplete, { ASSIGNEE_SOURCE_TYPES } from "./SubcontractAssigneeAutocomplete";
import { openDocumentPreviewInNewTab } from "utils/documentPreview";
import { formatInputDate, formatDate } from "utils/formatters";
import { CONTRACT_STATUSES } from "constants/projectRules";

const EMPTY_LIST = [];
const EMPTY_DOC_UPLOAD = { file: null, documentType: "", description: "" };
const REQUIRED_GENERAL_FIELDS = [
  ["subcontractNumber", "Số quyết định giao khoán"],
  ["subcontractName", "Tên quyết định giao khoán"],
  ["contractId", "Hợp đồng chính"],
  ["assigneeSourceId", "Đơn vị thực hiện"],
  ["signDate", "Ngày ký"],
  ["startDate", "Ngày bắt đầu"],
  ["endDate", "Ngày kết thúc"],
  ["totalValue", "Tổng giá trị quyết định giao khoán"],
  ["status", "Trạng thái"],
];
const ITEM_DISTRIBUTION_TYPES = [
  { value: "ESTIMATE", label: "Dự toán đơn" },
  { value: "COMMUNE_DETAIL", label: "Dự toán phân cấp phường (xã)" },
  { value: "PROVINCE_COMMUNE", label: "Dự toán phân cấp tỉnh - xã" },
  // { value: "ALL", label: "Tất cả" },
];
const DEFAULT_SUBCONTRACT_FORM = {
  status: CONTRACT_STATUSES[0].value,
  totalValue: 0,
  currency: "VND",
  itemDistributionType: "ESTIMATE",
  assigneeSourceType: ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP,
  assigneeSourceId: "",
};

const getDocumentId = (document) => document?.documentId ?? document?.id;
const getDocumentTypeName = (item) =>
  item?.name ||
  item?.documentTypeName ||
  item?.documentType ||
  item?.Name ||
  item?.DocumentTypeName ||
  item?.DocumentType ||
  "";

const textEquals = (left, right) =>
  String(left || "").trim().toLowerCase() === String(right || "").trim().toLowerCase();

const getField = (item, camelName, pascalName) => item?.[camelName] ?? item?.[pascalName];
const getPersonnelId = (item) => item?.personnelId ?? item?.PersonnelId ?? item?.userId ?? item?.UserId;
const getTeamMemberId = (item) => item?.teamMemberId ?? item?.subcontractTeamId ?? item?.SubcontractTeamId;
const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;

const findAssigneeSourceId = (assignee, sourceType, executionGroups, departments, departmentUnits) => {
  if (sourceType === ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP) {
    const match = executionGroups.find((item) =>
      textEquals(getField(item, "subcontractExecutionGroupCode", "SubcontractExecutionGroupCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "subcontractExecutionGroupName", "SubcontractExecutionGroupName"), assignee.assigneeName));
    return getField(match, "subcontractExecutionGroupId", "SubcontractExecutionGroupId") || "";
  }

  if (sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT) {
    const match = departments.find((item) =>
      textEquals(getField(item, "departmentCode", "DepartmentCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "departmentName", "DepartmentName"), assignee.assigneeName));
    return getField(match, "departmentId", "DepartmentId") || "";
  }

  if (sourceType === ASSIGNEE_SOURCE_TYPES.DEPARTMENT_UNIT) {
    const match = departmentUnits.find((item) =>
      textEquals(getField(item, "departmentUnitCode", "DepartmentUnitCode"), assignee.assigneeCode) ||
      textEquals(getField(item, "departmentUnitName", "DepartmentUnitName"), assignee.assigneeName));
    return getField(match, "departmentUnitId", "DepartmentUnitId") || "";
  }

  return "";
};

export default function SubcontractFormDialog({
  open,
  onClose,
  initialData,
  mode = "create",
  initialTab = 0,
  personnelData = EMPTY_LIST,
  documentsData = EMPTY_LIST,
  onDocumentsChanged,
  onSaved,
}) {
  const [tab, setTab] = useState(initialTab);
  const [form, setForm] = useState(initialData || DEFAULT_SUBCONTRACT_FORM);
  const [contracts, setContracts] = useState([]);
  const [subcontractGroups, setSubcontractGroups] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [executionGroups, setExecutionGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentUnits, setDepartmentUnits] = useState([]);
  const [activeAssigneeSourceType, setActiveAssigneeSourceType] = useState(ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP);
  const [loadingGeneral, setLoadingGeneral] = useState(false);
  const [personnel, setPersonnel] = useState(personnelData);
  const [roleOptions, setRoleOptions] = useState(["Thành viên"]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  const [documents, setDocuments] = useState(documentsData);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [docUpload, setDocUpload] = useState(EMPTY_DOC_UPLOAD);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [saving, setSaving] = useState(false);
  const initializedOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false;
      return;
    }

    if (initializedOpenRef.current) return;
    initializedOpenRef.current = true;

    setTab(initialTab);
    const nextForm = initialData || DEFAULT_SUBCONTRACT_FORM;
    setForm(nextForm);
    setActiveAssigneeSourceType(nextForm.assigneeSourceType || ASSIGNEE_SOURCE_TYPES.EXECUTION_GROUP);
    setPersonnel(personnelData);
    setDocuments(documentsData);
    setDocUpload(EMPTY_DOC_UPLOAD);
  }, [documentsData, initialData, initialTab, open, personnelData]);

  useEffect(() => {
    if (!open || tab !== 1) return;

    let active = true;
    const loadPersonnel = async () => {
      setLoadingPersonnel(true);
      try {
        const [personnelResult, roles] = await Promise.all([
          subcontractApi.getPersonnel(),
          subcontractApi.getRoleOptions(),
        ]);
        if (!active) return;

        const teamByPersonnelId = new Map(
          (personnelData || [])
            .map((member) => [getPersonnelId(member), member])
            .filter(([personnelId]) => personnelId != null),
        );

        const roleNames = roles.map((role) => role.name).filter(Boolean);
        setRoleOptions(roleNames.length ? roleNames : ["Thành viên"]);
        setPersonnel((currentPersonnel) => {
          const draftByPersonnelId = new Map(
            (currentPersonnel || [])
              .map((member) => [getPersonnelId(member), member])
              .filter(([personnelId]) => personnelId != null),
          );

          return (personnelResult.data || []).map((item) => {
            const personnelId = getPersonnelId(item);
            const existing = teamByPersonnelId.get(personnelId);
            const draft = draftByPersonnelId.get(personnelId);
            return {
              ...item,
              personnelId,
              selected: draft?.selected ?? Boolean(existing),
              teamMemberId: getTeamMemberId(existing),
              roleInSubcontract: draft?.roleInSubcontract || existing?.roleInSubcontract || "Thành viên",
              displayOrder: draft?.displayOrder ?? existing?.displayOrder ?? item.displayOrder ?? 0,
            };
          });
        });
      } finally {
        if (active) setLoadingPersonnel(false);
      }
    };

    loadPersonnel();
    return () => { active = false; };
  }, [open, tab, personnelData]);

  useEffect(() => {
    if (!open || tab !== 0) return;

    let active = true;
    const loadGeneralOptions = async () => {
      setLoadingGeneral(true);
      try {
        const [
          contractResult,
          groupResult,
          provinceResult,
          executionGroupResult,
          departmentResult,
          departmentUnitResult,
        ] = await Promise.all([
          subcontractApi.getContracts(),
          subcontractApi.getSubcontractGroups(),
          subcontractApi.getProvinces(),
          subcontractApi.getExecutionGroups(),
          subcontractApi.getDepartments(),
          subcontractApi.getDepartmentUnits(),
        ]);
        if (!active) return;
        setContracts(contractResult.data || []);
        setSubcontractGroups(groupResult || []);
        setProvinces(provinceResult.data || []);
        setExecutionGroups(executionGroupResult || []);
        setDepartments(departmentResult || []);
        setDepartmentUnits(departmentUnitResult || []);

        const subcontractId = initialData?.subcontractId;
        if (subcontractId) {
          try {
            const assignee = await subcontractApi.getSubcontractAssignee(subcontractId);
            if (!active || !assignee?.assigneeSourceType) return;
            const sourceType = assignee.assigneeSourceType;
            const sourceId = findAssigneeSourceId(
              assignee,
              sourceType,
              executionGroupResult || [],
              departmentResult || [],
              departmentUnitResult || [],
            );
            if (sourceId) {
              setActiveAssigneeSourceType(sourceType);
              setForm((current) => ({
                ...current,
                assigneeSourceType: sourceType,
                assigneeSourceId: sourceId,
                assigneeName: assignee.assigneeName,
              }));
            }
          } catch {
            // Older subcontract records may not have assignee snapshots yet.
          }
        }
      } finally {
        if (active) setLoadingGeneral(false);
      }
    };

    loadGeneralOptions();
    return () => { active = false; };
  }, [initialData?.subcontractId, open, tab]);

  useEffect(() => {
    if (!open || tab !== 2) return;

    let active = true;
    const loadDocuments = async () => {
      setLoadingDocuments(true);
      try {
        const subcontractId = form?.subcontractId || initialData?.subcontractId;
        const [types, latestDocuments] = await Promise.all([
          subcontractApi.getDocumentTypes(),
          subcontractId ? subcontractApi.getDocuments(subcontractId) : Promise.resolve(documentsData),
        ]);

        if (!active) return;
        const typeOptions = types
          .map((item) => getDocumentTypeName(item))
          .filter(Boolean)
          .filter((name, index, arr) => arr.indexOf(name) === index);
        setDocumentTypes(typeOptions);
        if (mode === "edit") {
          setDocuments((currentDocuments) => {
            const localDocuments = (currentDocuments || []).filter((document) => document.isLocal);
            return [...(latestDocuments || []), ...localDocuments];
          });
        }
      } finally {
        if (active) setLoadingDocuments(false);
      }
    };

    loadDocuments();
    return () => { active = false; };
  }, [documentsData, form?.subcontractId, initialData?.subcontractId, mode, open, tab]);

  const handleSavePersonnel = async () => {
    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      const existingMembers = await subcontractApi.getTeam(subcontractId);
      const existingByPersonnelId = new Map(
        existingMembers
          .map((member) => [getPersonnelId(member), member])
          .filter(([personnelId]) => personnelId != null),
      );
      const selectedPersonnel = personnel.filter((item) => item.selected);
      const selectedIds = new Set(selectedPersonnel.map(getPersonnelId).filter((personnelId) => personnelId != null));
      const joinDate = form?.signDate || new Date().toISOString().slice(0, 10);

      for (const item of selectedPersonnel) {
        const personnelId = getPersonnelId(item);
        if (personnelId == null) {
          throw new Error("NhÃ¢n sá»± Ä‘Æ°á»£c chá»n khÃ´ng cÃ³ mÃ£ nhÃ¢n sá»± há»£p lá»‡.");
        }
        const existing = existingByPersonnelId.get(personnelId);
        const payload = {
          subcontractId,
          userId: personnelId,
          roleInSubcontract: item.roleInSubcontract || "Thành viên",
          displayOrder: Number(item.displayOrder) || 0,
          joinDate: existing?.joinDate || joinDate,
          endDate: existing?.endDate || null,
          isActive: existing?.isActive ?? true,
          notes: existing?.notes || "",
        };

        if (!existing) {
          await subcontractApi.addTeamMember(payload);
        } else if (
          existing.roleInSubcontract !== payload.roleInSubcontract ||
          Number(existing.displayOrder || 0) !== payload.displayOrder
        ) {
          await subcontractApi.updateTeamMember(getTeamMemberId(existing), payload);
        }
      }

      for (const member of existingMembers) {
        const personnelId = getPersonnelId(member);
        if (!selectedIds.has(personnelId)) {
          await subcontractApi.removeTeamMember(getTeamMemberId(member));
        }
      }

      await onSaved?.();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: getApiErrorMessage(error, "KhÃ´ng thá»ƒ cáº­p nháº­t nhÃ¢n sá»± giao khoÃ¡n."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const saveSelectedPersonnelForSubcontract = async (subcontractId) => {
    const selectedPersonnel = personnel.filter((item) => item.selected);
    const joinDate = form?.signDate || new Date().toISOString().slice(0, 10);

    for (const item of selectedPersonnel) {
      const personnelId = getPersonnelId(item);
      if (personnelId == null) {
        throw new Error("NhÃ¢n sá»± Ä‘Æ°á»£c chá»n khÃ´ng cÃ³ mÃ£ nhÃ¢n sá»± há»£p lá»‡.");
      }
      await subcontractApi.addTeamMember({
        subcontractId,
        userId: personnelId,
        roleInSubcontract: item.roleInSubcontract || "ThÃ nh viÃªn",
        displayOrder: Number(item.displayOrder) || 0,
        joinDate,
        endDate: null,
        isActive: true,
        notes: "",
      });
    }
  };

  const uploadLocalDocumentsForSubcontract = async (subcontractId) => {
    const localDocuments = documents.filter((document) => document.isLocal && document.file);
    for (const document of localDocuments) {
      await subcontractApi.uploadDocument({
        subcontractId,
        file: document.file,
        documentType: document.documentType,
        description: document.description,
      });
    }
  };

  const buildGeneralPayload = () => ({
    subcontractId: form.subcontractId || initialData?.subcontractId,
    subcontractNumber: form.subcontractNumber?.trim() || "",
    subcontractName: form.subcontractName?.trim() || "",
    contractId: form.contractId ? Number(form.contractId) : null,
    subcontractorId: null,
    signDate: form.signDate || null,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
    startDateAct: form.startDateAct || null,
    endDateAct: form.endDateAct || null,
    subcontractGroup: form.subcontractGroup || null,
    provinceId: form.provinceId ? Number(form.provinceId) : null,
    totalValue: form.totalValue !== "" && form.totalValue != null ? Number(form.totalValue) : 0,
    vat: form.vat !== "" && form.vat != null ? Number(form.vat) : null,
    description: form.description || null,
    status: form.status || "",
    currency: form.currency || "VND",
    subcontractFilePath: form.subcontractFilePath || null,
    parentSubcontractId: form.parentSubcontractId ? Number(form.parentSubcontractId) : null,
    itemDistributionType: form.itemDistributionType || "ESTIMATE",
    assigneeSourceType: form.assigneeSourceType,
    assigneeSourceId: form.assigneeSourceId,
  });

  const buildAssigneePayload = (subcontractId) => ({
    subcontractId,
    assigneeSourceType: form.assigneeSourceType,
    assigneeSourceId: form.assigneeSourceId ? Number(form.assigneeSourceId) : null,
  });

  const validateGeneralPayload = (payload) => {
    const missing = REQUIRED_GENERAL_FIELDS.find(([key]) => (
      payload[key] === "" || payload[key] === null || payload[key] === undefined
    ));
    if (missing) return `Vui lòng nhập ${missing[1]}.`;
    if (payload.startDate && payload.endDate && payload.startDate > payload.endDate) {
      return "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc.";
    }
    if (payload.vat !== null && (payload.vat < 0 || payload.vat > 100)) {
      return "VAT phải nằm trong khoảng 0 đến 100.";
    }
    if (mode !== "edit" && !payload.itemDistributionType) {
      return "Vui lòng chọn loại giao khoán.";
    }
    return "";
  };

  const handleSaveGeneral = async () => {
    const payload = buildGeneralPayload();
    const validationMessage = validateGeneralPayload(payload);
    if (validationMessage) {
      setSnackbar({ open: true, message: validationMessage, severity: "warning" });
      return;
    }

    setSaving(true);
    try {
      if (mode === "edit") {
        await subcontractApi.updateSubcontract(payload.subcontractId, payload);
        await subcontractApi.saveSubcontractAssignee(buildAssigneePayload(payload.subcontractId));
        setSnackbar({ open: true, message: "Cập nhật thông tin chung thành công.", severity: "success" });
      } else {
        const createdSubcontract = await subcontractApi.createSubcontract(payload);
        const createdSubcontractId = getField(createdSubcontract, "subcontractId", "SubcontractId");
        await subcontractApi.saveSubcontractAssignee(buildAssigneePayload(createdSubcontractId));
        if (personnel.some((item) => item.selected)) {
          await saveSelectedPersonnelForSubcontract(createdSubcontractId);
        }
        await uploadLocalDocumentsForSubcontract(createdSubcontractId);
        setSnackbar({ open: true, message: "Tạo quyết định giao khoán thành công.", severity: "success" });
      }
      await onSaved?.();
      onClose();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể lưu thông tin chung.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (tab === 0) {
      handleSaveGeneral();
      return;
    }
    if (mode !== "edit") {
      handleSaveGeneral();
      return;
    }
    if (tab === 1 && mode === "edit") {
      handleSavePersonnel();
      return;
    }
    onClose();
  };

  const refreshDocuments = async () => {
    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId) return;
    const latestDocuments = await subcontractApi.getDocuments(subcontractId);
    setDocuments(latestDocuments || []);
    onDocumentsChanged?.(latestDocuments || []);
  };

  const handleSelectDocumentFile = (event) => {
    const file = event.target.files?.[0] || null;
    setDocUpload((current) => ({ ...current, file }));
    event.target.value = "";
  };

  const handleAddDocument = async () => {
    if (!docUpload.file) {
      setSnackbar({ open: true, message: "Vui lòng chọn file đính kèm.", severity: "warning" });
      return;
    }

    if (!docUpload.documentType) {
      setSnackbar({ open: true, message: "Vui lòng chọn loại tài liệu.", severity: "warning" });
      return;
    }

    const subcontractId = form?.subcontractId || initialData?.subcontractId;
    if (!subcontractId || mode !== "edit") {
      setDocuments((items) => [
        ...items,
        {
          id: `local-${Date.now()}`,
          documentName: docUpload.file.name,
          documentType: docUpload.documentType,
          description: docUpload.description,
          uploadDate: new Date().toISOString(),
          createBy: "Local",
          file: docUpload.file,
          isLocal: true,
        },
      ]);
      setDocUpload(EMPTY_DOC_UPLOAD);
      return;
    }

    setSaving(true);
    try {
      await subcontractApi.uploadDocument({
        subcontractId,
        file: docUpload.file,
        documentType: docUpload.documentType,
        description: docUpload.description,
      });
      setDocUpload(EMPTY_DOC_UPLOAD);
      await refreshDocuments();
      setSnackbar({ open: true, message: "Tải lên tài liệu thành công.", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể tải lên tài liệu.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewDocument = (document) => {
    openDocumentPreviewInNewTab({
      file: document,
      isRemote: !document.isLocal,
      downloadFn: subcontractApi.downloadDocument,
      onAlert: setSnackbar,
    });
  };

  const handleDownloadDocument = (document) => {
    if (document.isLocal) {
      const url = URL.createObjectURL(document.file);
      const link = window.document.createElement("a");
      link.href = url;
      link.download = document.documentName || document.file?.name || "document";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }
    subcontractApi.downloadDocumentFile(document);
  };

  const handleDeleteDocument = async (document) => {
    if (document.isLocal) {
      setDocuments((items) => items.filter((item) => item.id !== document.id));
      return;
    }

    const documentId = getDocumentId(document);
    if (!documentId || !window.confirm("Xóa tài liệu đính kèm này?")) return;

    setSaving(true);
    try {
      await subcontractApi.deleteDocument(documentId);
      await refreshDocuments();
      setSnackbar({ open: true, message: "Xóa tài liệu thành công.", severity: "success" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể xóa tài liệu.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const selectedPersonnelCount = personnel.filter((item) => item.selected).length;
  const allPersonnelSelected = personnel.length > 0 && selectedPersonnelCount === personnel.length;
  const somePersonnelSelected = selectedPersonnelCount > 0 && selectedPersonnelCount < personnel.length;

  const handlePersonnelCheckAll = (checked) => {
    setPersonnel((items) => items.map((item, index) => ({
      ...item,
      selected: checked,
      roleInSubcontract: item.roleInSubcontract || "Thành viên",
      displayOrder: item.displayOrder ?? index + 1,
    })));
  };

  const personnelColumns = [
    {
      id: "select",
      header: "",
      size: 50,
      Header: () => (
        <Checkbox
          size="small"
          checked={allPersonnelSelected}
          indeterminate={somePersonnelSelected}
          onChange={(event) => handlePersonnelCheckAll(event.target.checked)}
        />
      ),
      Cell: ({ row }) => (
        <Checkbox
          size="small"
          checked={row.original.selected}
          onChange={() => setPersonnel((items) => items.map((item) => (
            item.personnelId === row.original.personnelId
              ? {
                ...item,
                selected: !item.selected,
                roleInSubcontract: item.roleInSubcontract || "Thành viên",
              }
              : item
          )))}
        />
      ),
    },
    {
      accessorKey: "displayOrder",
      header: "STT",
      size: 60,
      Cell: ({ cell }) => (
        <Typography variant="body2" align="center">
          {cell.getValue() || 0}
        </Typography>
      ),
    },
    {
      accessorKey: "fullName",
      header: "Họ và tên",
      size: 180,
    },
    {
      accessorKey: "title",
      header: "Chức vụ",
      size: 140,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      accessorKey: "departmentName",
      header: "Phòng ban",
      size: 160,
      Cell: ({ cell }) => cell.getValue() || "N/A",
    },
    {
      accessorKey: "enterpriseName",
      header: "Xí nghiệp",
      size: 120,
      Cell: ({ cell }) => (
        cell.getValue() || "N/A"
      ),
    },
    {
      accessorKey: "roleInSubcontract",
      header: "Vai trò trong dự án",
      size: 180,
      columnDefType: "display",
      Cell: ({ row }) => (
        <AppSelect
          label="Vai trò"
          value={row.original.roleInSubcontract || "Thành viên"}
          disabled={!row.original.selected}
          options={["Thành viên", ...roleOptions.filter((role) => role !== "Thành viên")]}
          onChange={(value) => setPersonnel((items) => items.map((item) => (
            item.personnelId === row.original.personnelId
              ? { ...item, roleInSubcontract: value, selected: true }
              : item
          )))}
          sx={{ minWidth: 180 }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ];

  const documentColumns = [
    {
      accessorKey: "documentName",
      header: "Tên tài liệu",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    { accessorKey: "documentType", header: "Loại tài liệu" },
    {
      accessorKey: "description",
      header: "Mô tả",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    {
      id: "uploadDate",
      header: "Ngày tải lên",
      accessorFn: (row) => row.uploadDate || row.createdAt,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    { accessorKey: "createBy", header: "Người tải" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth className="subcontract-dialog">
      <DialogTitle className="subcontract-dialog__title">
        <Box>
          <Typography variant="h6" fontWeight={750}>
            {mode === "edit" ? "Cập nhật quyết định giao khoán" : "Thêm quyết định giao khoán"}
          </Typography>
          <Typography variant="body2" color="text.secondary">Hoàn thiện thông tin theo từng nhóm nội dung.</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: "18px !important" }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} className="subcontract-main-tabs" variant="scrollable">
          <Tab label="Thông tin chung" />
          <Tab label="Nhân sự" />
          <Tab label="Tài liệu đính kèm" />
        </Tabs>

        <Box sx={{ pt: 2.5 }}>
          {tab === 0 && (
            <Box className="subcontract-form-grid">
              <AppAutocomplete
                required
                className="full"
                label="Hợp đồng chính"
                value={form.contractId || ""}
                disabled={loadingGeneral}
                options={contracts.map((item) => ({
                  value: item.contractId,
                  label: `${item.contractNumber || ""}${item.contractNumber && item.contractName ? " - " : ""}${item.contractName || ""}`,
                }))}
                onChange={(value) => setForm({ ...form, contractId: value })}
              />
              <AppTextField
                required
                label="Tên quyết định giao khoán"
                value={form.subcontractName || ""}
                onChange={(value) => setForm({ ...form, subcontractName: value })}
                className="full"
              />
              <AppTextField
                required
                label="Số quyết định giao khoán"
                value={form.subcontractNumber || ""}
                onChange={(value) => setForm({ ...form, subcontractNumber: value })}
              />
              {mode !== "edit" && (
                <AppSelect
                  required
                  label="Loại dự toán"
                  value={form.itemDistributionType || "ESTIMATE"}
                  options={ITEM_DISTRIBUTION_TYPES}
                  onChange={(value) => setForm({ ...form, itemDistributionType: value || "ESTIMATE" })}
                />
              )}
              <SubcontractAssigneeAutocomplete
                required
                value={{
                  sourceType: form.assigneeSourceType,
                  sourceId: form.assigneeSourceId,
                }}
                activeSourceType={activeAssigneeSourceType}
                onActiveSourceTypeChange={(sourceType) => {
                  setActiveAssigneeSourceType(sourceType);
                  setForm((prev) => ({
                    ...prev,
                    assigneeSourceType: sourceType,
                    assigneeSourceId: "",
                    assigneeName: "",
                  }));
                }}
                executionGroups={executionGroups}
                departments={departments}
                departmentUnits={departmentUnits}
                disabled={loadingGeneral}
                onChange={(option) => setForm((prev) => ({
                  ...prev,
                  assigneeSourceType: option?.sourceType || prev.assigneeSourceType || activeAssigneeSourceType,
                  assigneeSourceId: option?.sourceId || "",
                  assigneeName: option?.name || "",
                }))}
              />
              <AppTextField required label="Ngày ký" type="date" value={formatInputDate(form.signDate)} onChange={(value) => setForm({ ...form, signDate: value })} />
              <AppTextField required label="Ngày bắt đầu" type="date" value={formatInputDate(form.startDate)} onChange={(value) => setForm({ ...form, startDate: value })} />
              <AppTextField required label="Ngày kết thúc" type="date" value={formatInputDate(form.endDate)} onChange={(value) => setForm({ ...form, endDate: value })} />
              <AppTextField label="Ngày bắt đầu thực tế" type="date" value={formatInputDate(form.startDateAct)} onChange={(value) => setForm({ ...form, startDateAct: value })} />
              <AppTextField label="Ngày kết thúc thực tế" type="date" value={formatInputDate(form.endDateAct)} onChange={(value) => setForm({ ...form, endDateAct: value })} />
              <AppSelect
                required
                label="Trạng thái"
                value={form.status || ""}
                options={CONTRACT_STATUSES}
                onChange={(value) => setForm({ ...form, status: value })}
              />
              <AppAutocomplete
                label="Tỉnh/Thành phố"
                value={form.provinceId || ""}
                disabled={loadingGeneral}
                options={provinces.map((item) => ({ value: item.provinceId, label: item.provinceName }))}
                onChange={(value) => setForm({ ...form, provinceId: value })}
              />
              <AppTextField label="Tổng giá trị quyết định giao khoán" type="number" value={form.totalValue ?? 0} disabled onChange={(value) => setForm({ ...form, totalValue: value })} />
              <AppSelect
                label="Tiền tệ"
                value={form.currency || "VND"}
                options={["VND", "USD"]}
                onChange={(value) => setForm({ ...form, currency: value })}
              />
              {/* <AppSelect
                label="Loại giao khoán"
                value={form.subcontractGroup || ""}
                allowClear
                disabled={loadingGeneral}
                options={subcontractGroups.map((item) => item.name || item.Name).filter(Boolean)}
                onChange={(value) => setForm({ ...form, subcontractGroup: value })}
              /> */}
              <AppTextField className="full" label="Mô tả" multiline minRows={3} value={form.description || ""} onChange={(value) => setForm({ ...form, description: value })} />
            </Box>
          )}
          {tab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Chọn nhân sự để thêm vào quyết định giao khoán; bỏ chọn tương ứng với loại khỏi danh sách.
              </Typography>
              {loadingPersonnel ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 260 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <DataTable columns={personnelColumns} data={personnel} enablePagination={false} />
              )}
            </Box>
          )}
          {tab === 2 && (
            <Box className="subcontract-stack">
              <Box className="subcontract-form-grid">
                <label className="subcontract-upload full">
                  <AttachFileOutlined />
                  <Typography variant="body2" fontWeight={650}>
                    {docUpload.file?.name || "Chọn file đính kèm *"}
                  </Typography>
                  <input type="file" hidden onChange={handleSelectDocumentFile} />
                </label>
                <AppSelect
                  required
                  label="Loại tài liệu"
                  value={docUpload.documentType}
                  options={documentTypes}
                  allowClear
                  onChange={(value) => setDocUpload((current) => ({ ...current, documentType: value }))}
                />
                <AppTextField
                  label="Mô tả"
                  value={docUpload.description}
                  onChange={(value) => setDocUpload((current) => ({ ...current, description: value }))}
                />
                <Box className="full" sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <AppButton
                    variant="contained"
                    startIcon={<AttachFileOutlined />}
                    onClick={handleAddDocument}
                    disabled={saving}
                  >
                    Thêm tài liệu
                  </AppButton>
                </Box>
              </Box>
              {/* Mock cũ: <DataTable columns={documentColumns} data={mockDocuments} /> */}
              {loadingDocuments ? (
                <Box sx={{ display: "grid", placeItems: "center", minHeight: 220 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <DataTable
                  columns={documentColumns}
                  data={documents}
                  enableRowActions
                  renderRowActions={({ row }) => (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Xem trước">
                        <IconButton size="small" onClick={() => handlePreviewDocument(row.original)}>
                          <VisibilityOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Tải xuống">
                        <IconButton size="small" onClick={() => handleDownloadDocument(row.original)}>
                          <DownloadOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Xóa">
                        <IconButton size="small" color="error" onClick={() => handleDeleteDocument(row.original)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                />
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
        <AppButton onClick={onClose} color="inherit">Hủy</AppButton>
        <AppButton variant="contained" startIcon={<SaveOutlined />} onClick={handleSave} disabled={saving}>
          {mode === "edit" ? "Cập nhật" : "Tạo mới"}
        </AppButton>
      </DialogActions>
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Dialog>
  );
}
